import React, { useEffect, useRef, useState, useContext } from 'react';
import { StateContext } from '../../state/stateContext';
import { T } from '@ihs-widget/utils/enums';

const CustomScrollbar: React.FC<{
  paddingTopOff: boolean;
  children: React.ReactNode;
  viewId: string;
  forceRefresh?: number;
  height?: string;
}> = ({
  paddingTopOff,
  children,
  viewId,
  forceRefresh = 0,
  height = 'h-auto'
}) => {
    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const [isScrolling, setIsScrolling] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [visibleHeight, setVisibleHeight] = useState(0);
    const [totalHeight, setTotalHeight] = useState(0);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollButtonHeight, setScrollButtonHeight] = useState(80);
    const isInitialMount = useRef(true);

    function getProportionalValueOfScroll(totalHeight: number, visibleHeight: number, inputValue: number, scrollButtonHeight: number) {
        if (inputValue < 0 || inputValue > totalHeight) {
            return 0;
        }

        let proportionalValue = (inputValue / (totalHeight - visibleHeight)) * (visibleHeight - scrollButtonHeight);

        const maxValue = visibleHeight - scrollButtonHeight - 1;
        if (proportionalValue > maxValue) {
            proportionalValue = maxValue;
        }

        return proportionalValue;
    }

    useEffect(() => {
        if (containerRef.current && stContext?.scrollPositions[viewId] !== undefined) {
            setTimeout(() => {
                if (containerRef.current) {
                    const scrollFromBottom = stContext.scrollPositions[viewId];
                    containerRef.current.scrollTop = containerRef.current.scrollHeight - scrollFromBottom - containerRef.current.clientHeight;
                }
            }, 0);
        }
    }, [viewId, stContext?.scrollPositions[viewId]]);

    useEffect(() => {
        if (containerRef.current && stContext?.scrollPositions[viewId] !== undefined) {
            setTimeout(() => {
                if (containerRef.current && containerRef.current.scrollTop > 0) {
                    const scrollFromBottom = stContext.scrollPositions[viewId];
                    containerRef.current.scrollTop = containerRef.current.scrollHeight - scrollFromBottom - containerRef.current.clientHeight;
                } else {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = 0
                        setStContext((prevState: any) => ({
                            ...prevState,
                            scrollPositions: {
                                ...prevState.scrollPositions,
                                [viewId]: containerRef?.current?.scrollHeight
                            }
                        }));
                    }
                }
            }, 0);
        }
    }, [forceRefresh]);

    const handleScroll = () => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        setIsScrolling(true);
        if (containerRef.current) {
            const { scrollTop, clientHeight, scrollHeight } = containerRef.current;
            const scrollFromBottom = scrollHeight - scrollTop - clientHeight;
            const proportionalScrollVal = getProportionalValueOfScroll(scrollHeight, clientHeight, scrollTop, scrollButtonHeight);
            setScrollPosition(proportionalScrollVal);
            setVisibleHeight(clientHeight);
            setTotalHeight(scrollHeight);

            if (viewId !== T.TAB_LINEUP && viewId !== T.TAB_STATS && viewId !== T.TAB_PLAYERS && viewId !== T.TAB_OVERVIEW && !viewId.includes(T.TAB_H2H)) {
                setStContext((prevState: any) => ({
                    ...prevState,
                    scrollPositions: {
                        ...prevState.scrollPositions,
                        [viewId]: scrollFromBottom
                    }
                }));
            }
        }
        
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 200);
    };

    useEffect(() => {
        if (containerRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                if (containerRef.current) {
                    setVisibleHeight(containerRef.current.clientHeight);
                    setTotalHeight(containerRef.current.scrollHeight);
                }
            });

            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);

  return (
    <div 
    ref={containerRef}
    onScroll={handleScroll}
    className={`
        w-full ${height} pb-[8px] 
        ${paddingTopOff ? 'pt-0' : 'pt-[12px]'}   
        flex flex-col relative overflow-y-scroll overflow-x-hidden
        custom-scrollbar
    `}
    id={"custom-scrollbar"}
    style={{
        scrollbarWidth: "none",
        msOverflowStyle: 'none',
        overscrollBehavior: 'none',
    }}
>
    {isScrolling && (
        <div 
            className={`absolute right-0 top-0 bottom-0 w-[8px] pointer-events-none min-h-[${visibleHeight}px] h-[${visibleHeight}px]`}
            style={{
                background: 'transparent',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                zIndex: 1000
            }}
        >
            <div 
                className=""
                style={{
                    overflowY: 'scroll',
                    width: 'calc(100% + 17px)',
                    height: `${visibleHeight}px`,
                    minHeight: `${visibleHeight}px`,
                    maxHeight: `${visibleHeight}px`,
                    paddingRight: '17px',
                    boxSizing: 'content-box',
                    background: 'transparent'
                }}
            >
                <div 
                    style={{
                        height: `${visibleHeight}px`,
                        minHeight: `${visibleHeight}px`,
                        background: 'transparent',
                        width: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'fixed'
                    }}
                >
                    <div 
                        style={{
                            position: 'absolute',
                            top: `${(scrollPosition / visibleHeight) * 100}%`,
                            height: `${scrollButtonHeight}px`,
                            background: '#a8a19b',
                            borderRadius: '4px',
                            width: '3px',
                        }}
                    />
                </div>
            </div>
        </div>
    )}
    {children}
</div>
  );
};

export default CustomScrollbar;

